import { signOutAuth } from "@helpers/auth-provider";
import { useAppTheme } from "@hooks/useAppTheme";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { storage } from "@memorang/helpers/storage";
import DialogWrapper from "@memorang/ui/components/DialogWrapper";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "expo-router";
import { useState } from "react";
import { FlatList } from "react-native";
import {
	Button,
	Dialog,
	Portal,
	RadioButton,
	Searchbar,
	Text,
} from "react-native-paper";
import { Apps } from "types/apps";

type Props = {
	visible: boolean;
	handleClose: () => void;
	currentApp?: Apps;
};

const AppSelectionDialog = ({ visible, handleClose, currentApp }: Props) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [localSelectedApp, setLocalSelectedApp] = useState<Apps | undefined>(
		currentApp,
	);
	const [isSigningOut, setIsSigningOut] = useState(false);

	const setSelectedApp = useGlobalStore((state) => state.setSelectedApp);
	const setShowPaywall = useGlobalStore((state) => state.setShowPaywall);
	const appsList = Object.values(Apps);

	const queryClient = useQueryClient();

	const filteredApps = appsList.filter((app) =>
		app.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	const handleSearch = (query: string) => {
		setSearchQuery(query);
	};

	const handleConfirm = async () => {
		if (localSelectedApp) {
			setSelectedApp(localSelectedApp);
			setShowPaywall(false);
			setIsSigningOut(true);
			await signOutAuth();
			router.replace("/");
			queryClient.clear();
			storage.clearAll();
			setIsSigningOut(false);
		}
		handleClose();
	};

	const theme = useAppTheme();

	const noResults = filteredApps.length === 0;

	const isSameApp = localSelectedApp === currentApp;

	const cta = isSigningOut
		? `Switching to ${localSelectedApp}...`
		: isSameApp
			? "Select an app"
			: `Switch to ${localSelectedApp}`;

	return (
		<Portal>
			<DialogWrapper visible={visible} handleClose={handleClose}>
				<Dialog.Icon icon="apps" />
				<Dialog.Title
					style={{
						textAlign: "center",
					}}
				>
					Switch app
				</Dialog.Title>
				<Dialog.Content>
					<Searchbar
						placeholder={"Search for an app to switch to"}
						onChangeText={handleSearch}
						value={searchQuery}
						style={{
							marginBottom: 16,
							backgroundColor: theme.colors.background,
						}}
					/>
					{noResults ? (
						<Text
							style={{
								textAlign: "center",
							}}
						>
							{`No apps found for "${searchQuery}"`}
						</Text>
					) : (
						<FlatList
							data={filteredApps}
							keyExtractor={(item) => item}
							renderItem={({ item }) => (
								<RadioButton.Item
									label={item}
									value={item}
									status={localSelectedApp === item ? "checked" : "unchecked"}
									onPress={() => setLocalSelectedApp(item)}
								/>
							)}
							style={{
								maxHeight: 300,
							}}
						/>
					)}
				</Dialog.Content>
				<Dialog.Actions>
					<Button onPress={handleClose}>Cancel</Button>
					<Button
						onPress={handleConfirm}
						disabled={!localSelectedApp || isSigningOut || isSameApp}
						loading={isSigningOut}
						mode="contained"
						style={{
							minWidth: 120,
						}}
					>
						{cta}
					</Button>
				</Dialog.Actions>
			</DialogWrapper>
		</Portal>
	);
};

export default AppSelectionDialog;
